import { Component, NgZone, ChangeDetectorRef, OnInit, inject  } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { SettingsModalComponent } from '../settings-modal/settings-modal.component';
import Phaser from 'phaser';
import RexUIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import MainScene from '../Scenes/MainScene';
import MazeScene from '../Scenes/MazeScene';
import CatacombScene from '../Scenes/CatacombScene';
import RestInPiecesScene from '../Scenes/RestInPiecesScene';
import PumpHouseScene from '../Scenes/PumpHouseScene';
import HouseScene from '../Scenes/HouseScene';
import eventsCenter from 'src/classes/EventsCenter';
import { ActivatedRoute } from '@angular/router';
import { Place, PlacesList } from '../../classes/dataClasses/Places';
import { StateManager } from 'src/classes/StateManager';
import UIScene from 'src/classes/UI/UIScene';
import { Portal } from 'src/classes/Portal';
import PumpHouseLevel2Scene from '../Scenes/PumpHouseLevel2Scene';
import ChurchHillScene from '../Scenes/ChurchHillScene';
import VCUEngineeringScene from '../Scenes/VCUEngineeringScene';
import PoeMuseumScene from '../Scenes/PoeMuseumScene';
import PoeMuseumBasementScene from '../Scenes/PoeMuseumBasement';
import CastleScene from '../Scenes/CastleScene';
import MonumentalChurchScene from '../Scenes/MonumentalChurchScene';
import { GlobalService } from '../services/global.service';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Conversation } from 'infrastructure/lib/src/models/conversation';
import MausoleumScene from '../Scenes/MausoleumScene';
import PowerPlantScene from '../Scenes/PowerPlantScene';
import BootScene from '../Scenes/BootScene';
import GenericScene from '../Scenes/GenericScene'
import { WebsocketService } from '../services/websocket.service';
import { SendMessageModalComponent } from '../send-message-modal/send-message-modal.component';
import { MessagesModalComponent } from '../messages-modal/messages-modal.component';
import { showToast } from 'src/classes/Utilities';
import { syncGameStateWithServer } from '../utils/game-state-manager';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage {
  static phaserGame: Phaser.Game;
  private static ngZone: NgZone;
  geoPlaces: Place[] = PlacesList;
  static modalCtrl: ModalController;
  static settingsModal: HTMLIonModalElement;
  static settingsModalActive: boolean = false;
  static peerConversationModal: HTMLIonModalElement;
  static messageModalActive: boolean = false;
  static http: HttpClient;
  private route = inject(ActivatedRoute);
  private lat: number;
  private lon: number;
  session: AuthSession;

  constructor(
    private _ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private modalCtrl: ModalController,
    private http: HttpClient,
    public globalService: GlobalService,
    private platform: Platform,
    public websocketService: WebsocketService,
  ) {
    HomePage.ngZone = this._ngZone;
    HomePage.modalCtrl = modalCtrl;
    HomePage.http = http;

    // Used for debugging (remove to save progress)
    // Portal.resetGpsVisitedPlaces();

    Portal.loadGpsVisitedPlaces();

    Portal.loadVisitedPlaces();

    // Subscribe to newly discoverd places
    // and flag them on the list
    Portal.subscribe(() => {
      cdr.detectChanges();
    });

    GlobalService.platform = platform;
    if (this.platform.is('mobile') || this.platform.is('mobileweb')) {
      console.log('Running on a mobile device (native)');
      GlobalService.zoom = 0.8;
      GlobalService.device = "phone";
    }  else {
      console.log('Running on a desktop');
      GlobalService.zoom = 1.3;
      GlobalService.device = "computer";
    }

    this.globalService.$loggedIn.subscribe((loggedIn) => {
      if(loggedIn){
        this.onUserSignIn();
      }
    });

  }

  ngAfterViewInit() {
    // Enable WebSockets by default
    this.websocketService.connect();

    console.log('ngAfterViewInit:');
    this.lat = Number(this.route.snapshot.paramMap.get('lat'));
    this.lon = Number(this.route.snapshot.paramMap.get('lon'));
    setTimeout(() => { 
      this.initializePhaser();
      eventsCenter.on('placeSelected', StateManager.updateLocation, this);
      GlobalService.$activeScenes.next(HomePage.phaserGame.scene.getScenes(true));
      GlobalService.$openModalsCount.subscribe((count) => {
        console.log(`openModalsCount: ${count}`);
        // If no modals are opened then the scene should not be paused.
        if(count <= 0){
          console.log("No modals open, resuming paused scenes");
          GlobalService.$activeScenes.getValue().forEach((scene) => {
            scene.scene.resume();
          });
        }
        else{
          // Check for rising edge, when the first modal is opened and capture the active scenes.
          if(count === 1 && GlobalService.$previousModalsCount.getValue() === 0){
            GlobalService.$activeScenes.next(HomePage.phaserGame.scene.getScenes(true));
          }

          // If any modals are open, then pause the active scenes.
          console.log("At least one modal is open, pausing all active scenes");
          GlobalService.$activeScenes.getValue().forEach((scene) => {
            scene.scene.pause();
          });
        }
      });

      // Poll for conversation metadata every minute or so.
      setInterval(() => {
        this.requestConversationMetadata();
      }, 30000);

      // Add event listener for when the mail icon is clicked to open the messages modal.
      window.addEventListener('open-messages-modal', () => {
          // Open the messages modal programmatically
          // For example, using ModalController:
          console.log("Opening messages modal from UIScene");
          this.modalCtrl.create({
            component: MessagesModalComponent
        }).then(modal => modal.present());
      });
    }, 100)
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy');
    this.websocketService.closeConnection();
  }

  initializePhaser() {
    console.log('initializing phaser');
    HomePage.ngZone.runOutsideAngular(() => {
      const config: Phaser.Types.Core.GameConfig = {
        type: Phaser.AUTO,
        scale: {
          mode: Phaser.Scale.RESIZE,// FIT
          autoCenter: Phaser.Scale.CENTER_BOTH
        },
        parent: 'game',
        scene: [
          BootScene,
          new MainScene(this.lat, this.lon),
          MazeScene,
          ChurchHillScene,
          CatacombScene,
          RestInPiecesScene,
          PumpHouseScene,
          PumpHouseLevel2Scene,
          VCUEngineeringScene,
          PoeMuseumScene,
          PoeMuseumBasementScene,
          new CastleScene("Pratt's Castle L0", "pratts_castle_tilemap_l0", "assets/scenes/PrattsCastleL0.json"),
          new CastleScene("Pratt's Castle", "pratts_castle_tilemap_l1", "assets/scenes/PrattsCastleL1.json"),
          new CastleScene("Pratt's Castle L2", "pratts_castle_tilemap_l2", "assets/scenes/PrattsCastleL2.json"),
          new CastleScene("Pratt's Castle L3", "pratts_castle_tilemap_l3", "assets/scenes/PrattsCastleL3.json"),
          new MonumentalChurchScene("Theatre L0", "theatre_tilemap_l0", "assets/scenes/theaterInteriorL0.json", 0.4, 2.4),
          new MonumentalChurchScene("Monumental Church", "theatre_tilemap", "assets/scenes/theaterInterior.json"),
          new MonumentalChurchScene("Theatre L2", "theatre_tilemap_l2", "assets/scenes/theaterInteriorL2.json"),
          new MonumentalChurchScene("Theatre L3", "theatre_tilemap_l3", "assets/scenes/theaterInteriorL3.json"),
          new HouseScene("House 1", "house_tilemap", "assets/scenes/HouseScene.json"),
          new HouseScene("Kitchen 1", 'kitchen_tilemap', "assets/scenes/KitchenScene.json"),
          new HouseScene("Bathroom 1", 'bathroom_tilemap', "assets/scenes/BathroomScene.json"),
          new MausoleumScene("Mausoleum 1", 'mausoleum1', "assets/scenes/MausoleumScene.json"),
          new MausoleumScene("Richmond Vampire", 'richmond_vampire_tilemap_l1', "assets/scenes/RichmondVampireL1.json"),
          new MausoleumScene("Richmond Vampire L2", 'richmond_vampire_tilemap_l2', "assets/scenes/RichmondVampireL2.json"),
          new PowerPlantScene("Belle Isle Power Plant", "power_plant_tilemap_l1", "assets/scenes/PowerPlantL1.json"),
          new PowerPlantScene("Power Plant Room 1", "power_plant_tilemap_room1", "assets/scenes/PowerPlantRoom1.json"),
          new GenericScene({
            name: "Brandt Hall",
            tileWidth: 24,
            tileHeight: 24,
            tileMapName: "brandt_hall_lobby",
            tileMapPath: "assets/scenes/BrandtHallLobby.json",
            images: [{name: "DormTileset", path: "assets/sourceImages/Bedroom tileset_1.png"}],
            playerScale: 1,
            cameraZoom: 1,
          }),
          new GenericScene({
            name: "BrandtHallL2",
            tileWidth: 24,
            tileHeight: 24,
            tileMapName: "brandt_hall_l2",
            tileMapPath: "assets/scenes/BrandtHallL2.json",
            images: [{name: "DormTileset", path: "assets/sourceImages/Bedroom tileset_1.png"}],
            playerScale: 1,
            cameraZoom: 1,
          }),
          new GenericScene({
            name: "BrandtHallL3",
            tileWidth: 24,
            tileHeight: 24,
            tileMapName: "brandt_hall_l3",
            tileMapPath: "assets/scenes/BrandtHallL3.json",
            images: [{name: "DormTileset", path: "assets/sourceImages/Bedroom tileset_1.png"}],
            playerScale: 1,
            cameraZoom: 1,
          }),
          UIScene,
        ],
        plugins: {
          scene: [
            {
              key: 'rexUI',
              plugin: RexUIPlugin,
              mapping: 'rexUI',
            },
          ],
        },
        physics: {
          default: 'matter',
          matter: {
            debug: false,
            gravity: {
              y: 0.0,
              x: 0.0,
            },
          },
        },
      };
      HomePage.phaserGame = new Phaser.Game(config);
    });
  }

  static async openSettingsModal() {
    if(!HomePage.settingsModalActive){
      HomePage.settingsModal = await HomePage.modalCtrl.create({
        component: SettingsModalComponent,
      });
      HomePage.settingsModal.present().then(() => {
        HomePage.settingsModalActive = true;
      });
      HomePage.settingsModal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      })
    }
  }


  static async openPeerConversationModal(peerName: string, displayProfileButton=false) {
    
    // First make sure that this user is registered in the database.
    HomePage.http.get(`${environment.REST_API}/users?username=${peerName}`).subscribe({
      next: async data => {
        console.log(`Response from user-get-lambda:`, data);
        if(!HomePage.messageModalActive){
          HomePage.peerConversationModal = await HomePage.modalCtrl.create({
            component: SendMessageModalComponent,
            componentProps: { 
              recipient: peerName,
              displayProfileButton: displayProfileButton
            }
          });
          HomePage.peerConversationModal.present().then(() => {
            GlobalService.incrementModalCount();
            HomePage.messageModalActive = true;
          });
          HomePage.peerConversationModal.onDidDismiss().then(() => {
            GlobalService.decrementModalCount();
            HomePage.messageModalActive = false;
          })
        }
      },
      error: async err => {
        // If we get a 400 error back from the user-get-lambda then the user is not registered.
        console.warn(`User not found:`, err);
        await showToast({message: "This user is not registered so they can't chat.", duration: 2000});
      }
    });
  }

  async requestConversationMetadata(){
    const loggedIn = this.globalService.$loggedIn.getValue();
    if(loggedIn){
      this.session = await fetchAuthSession();
      this.http.get(`${environment.REST_API}/conversations`, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
      }).subscribe({
        next: data => {
          GlobalService.$conversationsMetadata.next(data["conversations"] as Array<Conversation>);
        },
        error: async error => {
          console.error('There was an error retrieving your conversations!', error);
        }
      });
    }
  }

  async onUserSignIn() {
    // Close and reopen WebSocket connection on sign-in
    this.websocketService.closeConnection();
    await this.websocketService.connect();
    await syncGameStateWithServer();
  }
}
